import { useEffect, useState } from "react";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PersonPicker } from "@/components/common/PersonPicker";
import { fmtDateTime, formatError } from "@/lib/api";

const NONE = "__none";

// Inline-edit field that autosaves on blur/change and shows status + "last changed by" hint.
export function AutoField({ label, field, value, type = "text", options = [], onSave, readOnly, change, className = "", personType, placeholder }) {
  const [v, setV] = useState(value ?? "");
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  useEffect(() => setV(value ?? ""), [value]);

  const commit = async (nv) => {
    const norm = nv === "" || nv === NONE ? null : nv;
    if (norm === (value ?? null) || (norm === null && (value === "" || value == null))) return;
    setState("saving");
    setErr("");
    try {
      await onSave({ [field]: norm });
      setState("saved");
      setTimeout(() => setState("idle"), 1500);
    } catch (e) {
      setState("error");
      setErr(formatError(e));
      setV(value ?? "");
    }
  };

  const tid = `field-${field}`;
  let control;
  if (type === "switch") {
    control = <Switch checked={!!value} disabled={readOnly} onCheckedChange={(c) => commit(c)} data-testid={tid} />;
  } else if (type === "select") {
    control = (
      <Select value={v || NONE} onValueChange={(nv) => { setV(nv); commit(nv); }} disabled={readOnly}>
        <SelectTrigger className="bg-white h-9" data-testid={tid}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>—</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  } else if (type === "person") {
    control = <PersonPicker value={value} onChange={(id) => commit(id)} disabled={readOnly} personType={personType} testId={tid} />;
  } else if (type === "textarea") {
    control = <Textarea value={v} disabled={readOnly} onChange={(e) => setV(e.target.value)} onBlur={() => commit(v)} className="bg-white" data-testid={tid} />;
  } else {
    control = (
      <Input type={type === "money" ? "text" : type} inputMode={type === "money" || type === "number" ? "decimal" : undefined} value={v} disabled={readOnly}
        placeholder={placeholder} onChange={(e) => setV(e.target.value)} onBlur={() => commit(v)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className={`bg-white h-9 ${type === "money" || type === "number" ? "font-mono" : ""}`} data-testid={tid} />
    );
  }

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs text-slate-600">{label}</Label>
        {state === "saving" && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
        {state === "saved" && <Check className="h-3 w-3 text-emerald-600" data-testid={`${tid}-saved`} />}
        {state === "error" && <AlertCircle className="h-3 w-3 text-red-500" />}
      </div>
      {type === "switch" ? <div className="h-9 flex items-center">{control}</div> : control}
      {err && <p className="text-[11px] text-red-600" data-testid={`${tid}-error`}>{err}</p>}
      {!err && change && <p className="text-[11px] text-slate-400 truncate" title={`${change.by} · ${fmtDateTime(change.at)}`}>Last changed by {change.by} · {fmtDateTime(change.at)}</p>}
    </div>
  );
}
